import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { useDialog } from './DialogContext';
import { API_BASE_URL } from './config';
import { canManageTemplates } from './roles';
import { normalizeSchema } from './formSchemaUtils';
import { flattenFields } from './formSections';
import PromptModal from './PromptModal';
import CreateTemplateModal from './CreateTemplateModal';
import TemplateCanvas from './templates/TemplateCanvas';
import TemplateInspector from './templates/TemplateInspector';
import SourceList from './templates/SourceList';
import BoxPickerModal from './templates/BoxPickerModal';
import { Icon, sourceIconName } from './templates/icons';
import { useTemplateLayout } from './templates/useTemplateLayout';
import { usePaletteDrag } from './templates/usePaletteDrag';
import {
  DEFAULT_GRID,
  GRID_OPTIONS,
  MAX_BOXES,
  PAGE_WIDTH,
  addBox,
  bindBox,
  bindingFor,
  blankBox,
  copyBox,
  createBox,
  defaultBoxSize,
  findBox,
  findFreeSpot,
  groupFieldsBySection,
  layoutIssues,
  normalizeLayout,
  nudgedRect,
  placedCounts,
  rebindLayout,
  refreshFieldNames,
  removeBox,
  sourceTitle,
  updateBox,
} from './templates/templateLayout';
import { CARD, GHOST_ICON_BTN, PRIMARY_BTN, SECONDARY_BTN } from './ui';

const ZOOM_OPTIONS = [
  { value: 'fit', label: 'Fit' },
  { value: '0.5', label: '50%' },
  { value: '0.75', label: '75%' },
  { value: '1', label: '100%' },
  { value: '1.25', label: '125%' },
  { value: '1.5', label: '150%' },
];
// 100% is the sheet at its real size on a 96 dpi screen.
const PIXELS_PER_POINT = 96 / 72;
// The canvas well's padding and the rulers' gutter, which the page has to fit
// beside when it is fitted to the width.
const CANVAS_CHROME = 32 + 24 + 4;

const NO_FORMS = [];

const ARROWS = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };

const SIDE_PANEL = `${CARD} lg:sticky lg:top-20 lg:self-start lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto`;

const parseSchema = (raw) => {
  if (typeof raw !== 'string') return raw && typeof raw === 'object' ? raw : {};
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
};

// Below the widest screens the palette and the inspector take turns in one
// sidebar beside the page, so the page keeps its room; these switch between
// them. Picking a box on the page brings its settings up by itself.
const SideTabs = ({ value, onChange, boxSelected, className = '' }) => (
  <div role="tablist" aria-label="Side panel" className={`xl:hidden ${className}`}>
    <div className="grid grid-cols-2 gap-1 rounded-lg border border-gray-600 bg-gray-900/40 p-0.5">
      {[
        { value: 'add', label: 'Add to the page' },
        { value: 'box', label: boxSelected ? 'Selected box' : 'Page' },
      ].map((tab) => (
        <button
          key={tab.value}
          type="button"
          role="tab"
          aria-selected={value === tab.value}
          onClick={() => onChange(tab.value)}
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            value === tab.value
              ? 'bg-purple-500 text-white shadow'
              : 'bg-transparent text-gray-300 hover:bg-gray-700 hover:text-white'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  </div>
);

const questionsOf = (form) => (form ? normalizeSchema(parseSchema(form.schema)).fields : []);

// What's compared to tell whether there is anything to save.
const snapshotOf = (name, formId, layout) => JSON.stringify({ name, formId, layout });

const isTypingIn = (target) =>
  target instanceof HTMLElement &&
  (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));

// Width of an element, kept current as the window resizes. Takes the node
// rather than a ref so it starts measuring when the element first appears,
// which is after the template has loaded.
const useElementWidth = (node) => {
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    if (!node) return undefined;
    const measure = () => setWidth(node.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);
  return width;
};

// What pointing the template at another form did to its boxes.
const rebindNotice = ({ kept, matched, lost }, formName) => {
  const total = kept + matched + lost;
  const start = `Now filled in from ${formName}.`;
  if (total === 0) return { tone: 'info', text: start };
  if (lost === 0) {
    return {
      tone: 'info',
      text: `${start} All ${total} question ${total === 1 ? 'box' : 'boxes'} found ${total === 1 ? 'its' : 'their'} question${matched ? ` (${matched} matched by name)` : ''}.`,
    };
  }
  return {
    tone: 'warn',
    text: `${start} ${kept + matched} of ${total} question boxes found their question by name; ${lost} need a new one — they’re marked in red.`,
  };
};

const TemplateBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { alert, confirm } = useDialog();
  const { layout, commit, reset, undo, redo, canUndo, canRedo } = useTemplateLayout();

  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [formId, setFormId] = useState(null);
  const [form, setForm] = useState(null);
  const [entries, setEntries] = useState([]);
  // The form catalog, fetched the first time the author goes to change forms.
  const [forms, setForms] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [mode, setMode] = useState('edit');
  const [grid, setGrid] = useState(DEFAULT_GRID);
  const [zoom, setZoom] = useState('fit');
  // Which half of the sidebar shows below the widest screens: 'add' or 'box'.
  const [sideTab, setSideTab] = useState('add');
  // The "what goes in this box?" picker: { boxId, isNew }.
  const [picker, setPicker] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const [choosingForm, setChoosingForm] = useState(false);
  const [notice, setNotice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  // The box whose text the inspector should put the cursor in, once.
  const [focusTextFor, setFocusTextFor] = useState(null);
  const [wellNode, setWellNode] = useState(null);
  const wellWidth = useElementWidth(wellNode);
  const pageRef = useRef(null);

  const fields = useMemo(() => flattenFields(entries), [entries]);
  const fieldsById = useMemo(() => new Map(fields.map((field) => [field.id, field])), [fields]);
  const groups = useMemo(() => groupFieldsBySection(entries), [entries]);
  const issues = useMemo(() => layoutIssues(layout, fields), [layout, fields]);
  const issueById = useMemo(() => new Map(issues.map((issue) => [issue.box.id, issue.message])), [issues]);
  const placed = useMemo(() => placedCounts(layout), [layout]);
  // Only questions still on the form count towards it being filled in.
  const placedFieldCount = fields.filter((field) => placed.has(`field:${field.id}`)).length;
  const sampleContext = useMemo(() => ({ formName: form?.name, userEmail: user?.email }), [form, user]);
  const selectedBox = selectedId ? findBox(layout, selectedId) : null;
  const dirty = savedSnapshot !== null && snapshotOf(name, formId, layout) !== savedSnapshot;

  const fitScale = wellWidth ? (wellWidth - CANVAS_CHROME) / PAGE_WIDTH : 1;
  const scale = zoom === 'fit'
    ? Math.min(Math.max(fitScale, 0.35), 1.75)
    : Number(zoom) * PIXELS_PER_POINT;

  useEffect(() => {
    if (!user || !canManageTemplates(user.role)) {
      navigate('/forms');
      return;
    }

    const fetchTemplate = async () => {
      try {
        const { data: template } = await axios.get(`${API_BASE_URL}/api/templates/${id}`);
        let formRecord = null;
        if (template.form_id) {
          try {
            formRecord = (await axios.get(`${API_BASE_URL}/api/forms/${template.form_id}`)).data;
          } catch (error) {
            console.error('Failed to load the template’s form:', error);
          }
        }
        const questions = questionsOf(formRecord);
        // Names are brought up to date as the template opens, without making
        // that a change to save: it is what the template already says.
        const loaded = refreshFieldNames(normalizeLayout(template.layout), flattenFields(questions));
        setName(template.name);
        setFormId(template.form_id);
        setForm(formRecord);
        setEntries(questions);
        reset(loaded);
        setSavedSnapshot(snapshotOf(template.name, template.form_id, loaded));
      } catch (error) {
        console.error('Failed to fetch template:', error);
        await alert({
          title: 'Error',
          message: error.response?.status === 403
            ? 'You don’t have access to this template.'
            : 'Failed to load template',
        });
        navigate('/templates');
      } finally {
        setLoading(false);
      }
    };

    fetchTemplate();
  }, [id, user, navigate, reset, alert]);

  // A box undone out of existence takes the selection with it.
  useEffect(() => {
    if (selectedId && !findBox(layout, selectedId)) setSelectedId(null);
  }, [layout, selectedId]);

  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const atLimit = () => {
    if (layout.boxes.length < MAX_BOXES) return false;
    alert({ title: 'Page is full', message: `A template holds at most ${MAX_BOXES} boxes.` });
    return true;
  };

  // A box picked on the page (or drawn there) brings up its settings.
  const selectFromPage = (boxId) => {
    setSelectedId(boxId);
    if (boxId) setSideTab('box');
  };

  const placeBox = (source, rect) => {
    if (atLimit()) return;
    const box = createBox(source, rect);
    commit((current) => addBox(current, box));
    setSelectedId(box.id);
    setMode('edit');
    if (source.kind === 'text') setFocusTextFor(box.id);
  };

  const sizeOf = useCallback((source) => defaultBoxSize(bindingFor(source)), []);

  const { drag, itemProps, shouldIgnoreClick } = usePaletteDrag({
    pageRef,
    scale,
    grid,
    enabled: true,
    sizeOf,
    onDrop: placeBox,
  });

  // A click on the palette (or Enter on it) drops the entry in the next open
  // spot, for anyone who would rather not drag.
  const handlePaletteClick = (source) => {
    if (shouldIgnoreClick()) return;
    placeBox(source, findFreeSpot(layout.boxes, sizeOf(source), grid));
  };

  const handleDraw = (rect) => {
    if (atLimit()) return;
    const box = blankBox(rect);
    commit((current) => addBox(current, box));
    selectFromPage(box.id);
    setPicker({ boxId: box.id, isNew: true });
  };

  const openPicker = (boxId) => {
    const box = findBox(layout, boxId);
    if (!box) return;
    setSelectedId(boxId);
    // Text is changed where it's typed, not by choosing something else.
    if (box.kind === 'text') setFocusTextFor(boxId);
    else setPicker({ boxId, isNew: false });
  };

  const handlePick = (source) => {
    const target = picker;
    setPicker(null);
    if (!target) return;
    commit((current) => updateBox(current, target.boxId, (box) => bindBox(box, source)));
    setSelectedId(target.boxId);
    if (source.kind === 'text') setFocusTextFor(target.boxId);
  };

  // Dismissing the picker for a box that was only just drawn undoes the
  // drawing: nothing was chosen for it, so nothing should be left behind.
  const handlePickerCancel = () => {
    const target = picker;
    setPicker(null);
    if (target?.isNew) undo();
  };

  const handleCommitRect = (boxId, rect) => commit((current) => updateBox(current, boxId, rect));

  // `key` groups a run of edits to one setting (typing a label, stepping a
  // measurement) into a single step to undo.
  const handleBoxChange = (patch, key = null) => {
    if (!selectedId) return;
    commit((current) => updateBox(current, selectedId, patch), key && `${selectedId}:${key}`);
  };

  const duplicateSelected = () => {
    if (!selectedBox || atLimit()) return;
    const copy = copyBox(selectedBox, grid || 9);
    commit((current) => addBox(current, copy));
    setSelectedId(copy.id);
  };

  const deleteSelected = () => {
    if (!selectedId) return;
    const boxId = selectedId;
    commit((current) => removeBox(current, boxId));
    setSelectedId(null);
  };

  const showNextIssue = () => {
    if (issues.length === 0) return;
    const current = issues.findIndex((issue) => issue.box.id === selectedId);
    setMode('edit');
    selectFromPage(issues[(current + 1) % issues.length].box.id);
  };

  const handleSave = async () => {
    if (saving) return;
    const sent = snapshotOf(name, formId, layout);
    setSaving(true);
    try {
      await axios.put(`${API_BASE_URL}/api/templates/${id}`, {
        name,
        layout,
        ...(formId ? { form_id: formId } : {}),
      });
      setSavedSnapshot(sent);
    } catch (error) {
      console.error('Failed to save template:', error);
      alert({ title: 'Error', message: error.response?.data?.msg || 'Failed to save template' });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = async () => {
    if (dirty) {
      const leave = await confirm({
        title: 'Leave without saving?',
        message: 'Your changes to this template haven’t been saved. Leave anyway and lose them?',
        confirmLabel: 'Leave without saving',
        cancelLabel: 'Keep editing',
        danger: true,
      });
      if (!leave) return;
    }
    navigate('/templates');
  };

  const handleRenameConfirm = (rawName) => {
    const trimmed = rawName.trim();
    if (trimmed) setName(trimmed.slice(0, 100));
    setRenaming(false);
  };

  const openFormChooser = async () => {
    if (!forms) {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/api/forms`);
        setForms(data);
      } catch (error) {
        console.error('Failed to fetch forms:', error);
        alert({ title: 'Error', message: 'Failed to load your forms' });
        return;
      }
    }
    setChoosingForm(true);
  };

  // Boxes go with the template to the new form: each keeps its question where
  // the form has it, finds one of the same name where it doesn't, and is
  // marked for the author otherwise.
  const handleFormChosen = async ({ form_id: nextFormId }) => {
    setChoosingForm(false);
    if (nextFormId === formId) return;
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/forms/${nextFormId}`);
      const questions = questionsOf(data);
      const nextFields = flattenFields(questions);
      const outcome = rebindLayout(layout, nextFields);
      commit((current) => rebindLayout(current, nextFields).layout);
      setForm(data);
      setFormId(data.id);
      setEntries(questions);
      setNotice(rebindNotice(outcome, data.name));
    } catch (error) {
      console.error('Failed to load form:', error);
      alert({ title: 'Error', message: 'Failed to load that form' });
    }
  };

  // Keyboard shortcuts for the page. They stand aside while a dialog is open
  // and while the author is typing, except Save, which works from anywhere.
  useEffect(() => {
    const onKeyDown = (event) => {
      const mod = event.metaKey || event.ctrlKey;
      const key = event.key.toLowerCase();
      if (document.querySelector('[aria-modal="true"]')) return;
      if (mod && key === 's') {
        event.preventDefault();
        handleSave();
        return;
      }
      if (isTypingIn(event.target)) return;
      if (mod && key === 'z') {
        event.preventDefault();
        if (event.shiftKey) redo();
        else undo();
        return;
      }
      if (mod && key === 'y') {
        event.preventDefault();
        redo();
        return;
      }
      if (mode !== 'edit' || !selectedBox) return;
      if (event.key === 'Escape') {
        setSelectedId(null);
      } else if (mod && key === 'd') {
        event.preventDefault();
        duplicateSelected();
      } else if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        deleteSelected();
      } else if (event.key === 'Enter') {
        event.preventDefault();
        openPicker(selectedBox.id);
      } else if (ARROWS[event.key]) {
        event.preventDefault();
        const step = event.altKey || !grid ? 1 : grid;
        const [dx, dy] = ARROWS[event.key];
        commit(
          (current) => updateBox(current, selectedBox.id, (box) => ({ ...box, ...nudgedRect(box, dx * step, dy * step) })),
          `${selectedBox.id}:nudge`,
        );
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 py-8">
        <div className="text-center text-white">Loading template...</div>
      </div>
    );
  }

  const status = saving ? 'Saving…' : dirty ? 'Unsaved changes' : 'All changes saved';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 py-6">
      <BoxPickerModal
        open={!!picker}
        isNew={!!picker?.isNew}
        formName={form?.name}
        groups={groups}
        placed={placed}
        onPick={handlePick}
        onCancel={handlePickerCancel}
      />
      <PromptModal
        open={renaming}
        title="Rename template"
        message="This is the name the template is listed under. The new name is stored when you save."
        defaultValue={name}
        placeholder="Template name"
        confirmLabel="Rename"
        required
        onConfirm={handleRenameConfirm}
        onCancel={() => setRenaming(false)}
      />
      <CreateTemplateModal
        open={choosingForm}
        askName={false}
        title="Fill in from another form"
        message="Boxes keep their question when the new form has one with the same name. The rest are marked for you to choose again."
        confirmLabel="Use this form"
        forms={forms || NO_FORMS}
        defaultFormId={formId}
        onConfirm={handleFormChosen}
        onCancel={() => setChoosingForm(false)}
      />

      {/* What's being carried from the palette, until it's over the page —
          where the page shows the box itself. */}
      {drag && !drag.rect && (
        <div
          className="pointer-events-none fixed z-[60] flex items-center gap-2 rounded-lg bg-purple-600 px-3 py-1.5 text-sm text-white shadow-xl"
          style={{ left: drag.x + 14, top: drag.y + 10 }}
        >
          <Icon name={sourceIconName(drag.source)} className="w-4 h-4" />
          {sourceTitle(drag.source)}
        </div>
      )}

      <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="min-w-0">
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex items-center gap-1.5 bg-transparent hover:bg-transparent p-0 text-sm text-gray-400 hover:text-purple-300 transition-colors"
            >
              <Icon name="back" className="w-4 h-4" />
              Templates
            </button>
            <div className="mt-2 flex items-center gap-2 min-w-0">
              <h1 className="text-3xl font-bold text-white truncate">{name}</h1>
              <button
                type="button"
                onClick={() => setRenaming(true)}
                className={`${GHOST_ICON_BTN} shrink-0`}
                title="Rename this template"
                aria-label="Rename this template"
              >
                <Icon name="pencil" className="w-4 h-4" />
              </button>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-400">
              <Icon name="document" className="w-4 h-4 text-purple-300" />
              {form ? (
                <span>
                  Filled in from <span className="font-medium text-gray-200">{form.name}</span>
                </span>
              ) : (
                <span className="text-amber-300">No form to fill it in from</span>
              )}
              <button
                type="button"
                onClick={openFormChooser}
                className="bg-transparent hover:bg-transparent p-0 text-sm text-purple-300 hover:text-purple-200 hover:underline"
              >
                {form ? 'Change form' : 'Choose a form'}
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span
              className={`inline-flex items-center gap-1.5 text-sm ${dirty ? 'text-amber-300' : 'text-gray-400'}`}
              aria-live="polite"
            >
              {!dirty && !saving && <Icon name="check" className="w-4 h-4 text-emerald-400" strokeWidth={2} />}
              {status}
            </span>
            <button type="button" onClick={handleClose} className={SECONDARY_BTN}>
              Close
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className={`${PRIMARY_BTN} disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {saving ? 'Saving...' : 'Save Template'}
            </button>
          </div>
        </div>

        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_19rem] xl:grid-cols-[16rem_minmax(0,1fr)_19rem]">
          {/* Palette */}
          <aside
            className={`${SIDE_PANEL} ${sideTab === 'add' ? '' : 'hidden'} xl:block order-2 lg:order-none lg:col-start-2 lg:row-start-1 xl:col-start-1 p-3 max-h-[60vh] overflow-y-auto`}
          >
            <SideTabs value={sideTab} onChange={setSideTab} boxSelected={!!selectedBox} className="mb-3" />
            <div className="px-1.5 pb-3">
              <h2 className="text-sm font-semibold text-white">Add to the page</h2>
              <p className="mt-1 text-xs text-gray-400">
                Drag onto the page, or click to drop it in the next open spot.
              </p>
            </div>
            <SourceList
              formName={form?.name}
              groups={groups}
              placed={placed}
              onPick={handlePaletteClick}
              itemProps={itemProps}
              draggable
            />
          </aside>

          {/* The page */}
          <div className="min-w-0 order-1 lg:order-none lg:col-start-1 lg:row-start-1 xl:col-start-2">
            <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2">
              <div role="radiogroup" aria-label="View" className="inline-flex rounded-lg border border-gray-600 bg-gray-800/60 p-0.5">
                {[
                  { value: 'edit', label: 'Edit', icon: 'pencil' },
                  { value: 'preview', label: 'Preview', icon: 'document' },
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={mode === option.value}
                    onClick={() => setMode(option.value)}
                    className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                      mode === option.value
                        ? 'bg-purple-500 text-white shadow'
                        : 'bg-transparent text-gray-300 hover:bg-gray-700 hover:text-white'
                    }`}
                  >
                    <Icon name={option.icon} className="w-4 h-4" />
                    {option.label}
                  </button>
                ))}
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-400">
                Grid
                <select
                  value={grid}
                  onChange={(e) => setGrid(Number(e.target.value))}
                  disabled={mode !== 'edit'}
                  className="rounded-lg border border-gray-600 bg-gray-800/60 py-1.5 pl-3 text-sm text-white disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  {GRID_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-400">
                Zoom
                <select
                  value={zoom}
                  onChange={(e) => setZoom(e.target.value)}
                  className="rounded-lg border border-gray-600 bg-gray-800/60 py-1.5 pl-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  {ZOOM_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              <div className="ml-auto flex items-center gap-1">
                {issues.length > 0 && (
                  <button
                    type="button"
                    onClick={showNextIssue}
                    className="mr-2 inline-flex items-center gap-1.5 rounded-full border border-red-500/40 bg-red-500/10 hover:bg-red-500/20 px-3 py-1 text-xs font-medium text-red-200 transition-colors"
                    title="Show the next box that needs attention"
                  >
                    <Icon name="warning" className="w-4 h-4" />
                    {issues.length} {issues.length === 1 ? 'box needs' : 'boxes need'} attention
                  </button>
                )}
                <button
                  type="button"
                  onClick={undo}
                  disabled={!canUndo}
                  className={`${GHOST_ICON_BTN} disabled:bg-transparent disabled:opacity-40`}
                  title="Undo"
                  aria-label="Undo"
                >
                  <Icon name="undo" className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={redo}
                  disabled={!canRedo}
                  className={`${GHOST_ICON_BTN} disabled:bg-transparent disabled:opacity-40`}
                  title="Redo"
                  aria-label="Redo"
                >
                  <Icon name="redo" className="w-5 h-5" />
                </button>
              </div>
            </div>

            {!form && (
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
                <span>This template’s form was deleted, so its question boxes have nothing to fill them.</span>
                <button
                  type="button"
                  onClick={openFormChooser}
                  className="rounded-lg bg-amber-500/20 hover:bg-amber-500/30 px-3 py-1.5 text-sm font-medium text-amber-100"
                >
                  Choose a form
                </button>
              </div>
            )}
            {notice && (
              <div
                className={`mb-3 flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${
                  notice.tone === 'warn'
                    ? 'border-amber-500/40 bg-amber-500/10 text-amber-100'
                    : 'border-purple-500/30 bg-purple-500/10 text-purple-100'
                }`}
              >
                <span>{notice.text}</span>
                <button
                  type="button"
                  onClick={() => setNotice(null)}
                  className="bg-transparent hover:bg-transparent p-0 text-xs text-gray-300 hover:text-white underline-offset-2 hover:underline"
                >
                  Dismiss
                </button>
              </div>
            )}

            <div ref={setWellNode} className="overflow-x-auto rounded-xl border border-gray-700/60 bg-gray-950/40 p-4">
              <div className="w-max mx-auto">
                <TemplateCanvas
                  pageRef={pageRef}
                  boxes={layout.boxes}
                  fieldsById={fieldsById}
                  issueById={issueById}
                  selectedId={selectedId}
                  mode={mode}
                  scale={scale}
                  grid={grid}
                  sampleContext={sampleContext}
                  dropPreview={drag?.rect ? { rect: drag.rect, title: sourceTitle(drag.source) } : null}
                  onSelect={selectFromPage}
                  onCommitRect={handleCommitRect}
                  onDraw={handleDraw}
                  onOpenPicker={openPicker}
                />
              </div>
            </div>
          </div>

          {/* Inspector */}
          <aside
            className={`${SIDE_PANEL} ${sideTab === 'box' ? '' : 'hidden'} xl:block order-3 lg:order-none lg:col-start-2 lg:row-start-1 xl:col-start-3`}
            aria-label={selectedBox ? 'Selected box' : 'Page'}
          >
            <SideTabs value={sideTab} onChange={setSideTab} boxSelected={!!selectedBox} className="p-3 pb-0" />
            <TemplateInspector
              box={mode === 'edit' ? selectedBox : null}
              field={selectedBox?.kind === 'field' ? fieldsById.get(selectedBox.fieldId) : null}
              issue={selectedBox ? issueById.get(selectedBox.id) : null}
              focusText={!!selectedBox && focusTextFor === selectedBox.id}
              onTextFocused={() => setFocusTextFor(null)}
              fieldCount={fields.length}
              placedFieldCount={placedFieldCount}
              placed={placed}
              issues={issues}
              onSelectBox={(boxId) => {
                setMode('edit');
                selectFromPage(boxId);
              }}
              onChange={handleBoxChange}
              onOpenPicker={() => selectedBox && openPicker(selectedBox.id)}
              onDuplicate={duplicateSelected}
              onDelete={deleteSelected}
            />
          </aside>
        </div>
      </div>
    </div>
  );
};

export default TemplateBuilder;
